type ParamsMap = Map<string, string>;

interface ParsedUrlParams {
  raw: string;
  params: ParamsMap;
}

/** extracts params from a url string
 * urls can be parsed with 'new URL('https://www.moebel.de')'. BUT: this only
 * works if there is a protocol and a full url. So it would not be working for
 * relative urls or something like 'www.moebel.de'. This function can also
 * parse relative urls and splits it in query-params and the stuff before them */
export function parseUrlParams(url: string): ParsedUrlParams {
  const splitUrl = (url || '').split('?');
  const rawUrl = splitUrl[0];
  const urlParams = (splitUrl[1] || '').split('&');
  const paramsMap = urlParams.reduce((acc, cur) => {
    const keyVal = cur.split('=');
    const key = keyVal[0];
    if (key) {
      const val = keyVal[1];
      acc.set(key, val);
    }

    return acc;
  }, new Map());

  return {
    raw: rawUrl,
    params: paramsMap,
  };
}

/** converts a string with query params to a Map structure
 * @example parseQueryParams('color=')
 */
export function parseQueryParams(queryParams: string): ParamsMap {
  // trim potential start characters '/?'
  const adjustedQueryParamString = queryParams.replace(/^\/\?|^\/|^\?/, '');
  return parseUrlParams(`/?${adjustedQueryParamString}`).params;
}

export function toQueryParams(paramsMap: ParamsMap): string {
  return Array.from(paramsMap.entries())
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
}

export function combineUrl(part1: string, part2: string): string {
  const p1 = part1.endsWith('/') ? part1.substring(0, part1.length - 1) : part1;
  const p2 = part2.startsWith('/') ? part2.substring(1, part2.length) : part2;
  return `${p1}/${p2}`;
}

/** Adjusts a query param in an url string
 * @example
 * adjustUrlParam('color', 'peach', 'www.moebel.de/wohnen?color=orange')
 * returns 'www.moebel.de/wohnen?color=peach'
 */
export function adjustUrlParam(key: string, value: string, url: string): string {
  const curUrl = url;
  const urlData = parseUrlParams(curUrl);
  urlData.params.set(key, value);
  const newParams = toQueryParams(urlData.params);
  return urlData.raw + '?' + newParams;
}

export function adjustUrlParams(queryParams: ParamsMap, sourceUrl: string): string {
  return Array.from(queryParams.entries()).reduce((adjustedUrl, [key, value]) => {
    return adjustUrlParam(key, value, adjustedUrl);
  }, sourceUrl);
}

export function isFullUrl(link: string): boolean {
  return /^http[s]?:|^www\./.test(link);
}
